import React, { useState } from 'react';
import Icon from './Icon';
import { CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_HREF } from '../site';

const INTERESTS = ['Voice AI ordering', 'AI upselling', 'Self-order kiosk', 'Admin portal'];

// Submits to Netlify Forms; the matching static form lives in public/index.html.
const encode = (data) =>
  Object.keys(data)
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(data[k])}`)
    .join('&');

// Prefilled email so a lead isn't lost if the form backend is unreachable.
const mailtoFor = (data) => {
  const lines = [
    ['Name', data.name],
    ['Restaurant', data.restaurant],
    ['Email', data.email],
    ['Phone', data.phone],
    ['Locations', data.locations],
    ['Interested in', data.interests],
    ['Languages', data.languages],
  ]
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}: ${v}`)
    .join('\n');
  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Demo request')}&body=${encodeURIComponent(lines)}`;
};

export default function DemoCTA() {
  const [status, setStatus] = useState('idle');
  const [lastData, setLastData] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const fd = new FormData(form);
    const data = Object.fromEntries(fd.entries());
    data.interests = fd.getAll('interests').join(', ');
    delete data['bot-field'];
    setLastData(data);
    setStatus('sending');

    // Netlify Forms only exists on the deployed site; the local dev server 404s.
    if (process.env.NODE_ENV === 'development') {
      console.info('[demo form] dev mode, not submitted:', data);
      setStatus('sent');
      form.reset();
      return;
    }

    try {
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({ 'form-name': 'demo', ...data }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setStatus('sent');
      form.reset();
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="cta dark" id="demo">
      <div className="cta__glow" aria-hidden="true" />
      <div className="wrap cta__grid">
        <div className="cta__copy" data-reveal>
          <p className="eyebrow">Book a demo</p>
          <h2 className="h2">
            Hear your own menu <em>ordered back to you.</em>
          </h2>
          <p className="lead">
            Tell us about your restaurant. We'll walk you through a live call, the kiosk and the
            admin portal, and give you a straight answer on pricing.
          </p>
          <ul className="checks checks--light">
            <li>
              <Icon name="check" size={18} />
              <span>A 20-minute call, with no pressure</span>
            </li>
            <li>
              <Icon name="check" size={18} />
              <span>We do the setup, so you don't need any tech skills</span>
            </li>
            <li>
              <Icon name="check" size={18} />
              <span>
                Rather talk now? Call <a href={CONTACT_PHONE_HREF}>{CONTACT_PHONE}</a> or email{' '}
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              </span>
            </li>
          </ul>
        </div>

        <form className="form" name="demo" onSubmit={onSubmit} data-reveal>
          {status === 'sent' ? (
            <div className="form__done">
              <span className="icon-chip">
                <Icon name="check" />
              </span>
              <h3>Thanks, we'll be in touch shortly.</h3>
              <p>We usually reply within one business day.</p>
            </div>
          ) : (
            <>
              <p hidden>
                <label>
                  Don't fill this out: <input name="bot-field" />
                </label>
              </p>
              <div className="form__row">
                <label>
                  Your name
                  <input name="name" required autoComplete="name" />
                </label>
                <label>
                  Restaurant
                  <input name="restaurant" required autoComplete="organization" />
                </label>
              </div>
              <div className="form__row">
                <label>
                  Email
                  <input name="email" type="email" required autoComplete="email" />
                </label>
                <label>
                  Phone
                  <input name="phone" type="tel" autoComplete="tel" />
                </label>
              </div>
              <label>
                Number of locations
                <select name="locations" defaultValue="1">
                  <option value="1">1</option>
                  <option value="2-5">2–5</option>
                  <option value="6-20">6–20</option>
                  <option value="20+">20+</option>
                </select>
              </label>
              <fieldset>
                <legend>Interested in</legend>
                <div className="chips">
                  {INTERESTS.map((i) => (
                    <label key={i} className="chip">
                      <input type="checkbox" name="interests" value={i} defaultChecked={i === INTERESTS[0]} />
                      <span>{i}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <label>
                <span>
                  Languages your customers speak <span className="opt">(optional)</span>
                </span>
                <input name="languages" placeholder="e.g. English, Spanish, Chinese" />
              </label>
              <button className="btn btn--primary btn--lg btn--block" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : 'Book my demo'}
                <Icon name="arrow" size={18} />
              </button>
              {status === 'error' && (
                <div className="form__error" role="alert">
                  <p>We couldn't send that just now. Your details are still here, so one tap emails them to us.</p>
                  <a className="btn btn--ghost-dark" href={mailtoFor(lastData || {})}>
                    Email us these details
                  </a>
                </div>
              )}
            </>
          )}
        </form>
      </div>
    </section>
  );
}
